import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchThemePreference,
  getSignedInThemePreferenceUserId,
  persistThemePreference,
} from '../services/themePreferenceService';

const THEME_STORAGE_KEY = 'dashboard-theme-mode';
const DEFAULT_THEME_MODE = 'dark';

const ThemeContext = createContext({
  isDarkMode: true,
  setThemeMode: () => {},
  toggleTheme: () => {}
});

const getCurrentUserId = () => getSignedInThemePreferenceUserId();

const getThemeStorageKey = userId => {
  if (typeof window === 'undefined') {
    return THEME_STORAGE_KEY;
  }

  return userId ? `${THEME_STORAGE_KEY}:${userId}` : THEME_STORAGE_KEY;
};

const getStoredThemeMode = storageKey => {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_MODE;
  }

  const storedThemeMode = window.localStorage.getItem(storageKey);
  if (storedThemeMode === 'light' || storedThemeMode === 'dark') {
    return storedThemeMode;
  }

  return null;
};

const getInitialThemeMode = storageKey => {
  const storedThemeMode = getStoredThemeMode(storageKey);
  if (storedThemeMode) {
    return storedThemeMode;
  }

  const legacyThemeMode = storageKey !== THEME_STORAGE_KEY ? getStoredThemeMode(THEME_STORAGE_KEY) : null;
  if (legacyThemeMode) {
    return legacyThemeMode;
  }

  return DEFAULT_THEME_MODE;
};

export const ThemeProvider = ({ children }) => {
  const [currentUserId, setCurrentUserId] = useState(getCurrentUserId);
  const [storageKey, setStorageKey] = useState(() => getThemeStorageKey(getCurrentUserId()));
  const [themeMode, setThemeMode] = useState(() => getInitialThemeMode(getThemeStorageKey(getCurrentUserId())));
  const [isRemotePreferenceReady, setIsRemotePreferenceReady] = useState(() => !getCurrentUserId());
  const lastPersistedRemotePreferenceRef = useRef({ userId: null, themeMode: null });

  useEffect(() => {
    const syncThemeIdentity = () => {
      const nextUserId = getCurrentUserId();

      setCurrentUserId(currentUserIdValue => (currentUserIdValue === nextUserId ? currentUserIdValue : nextUserId));
      setStorageKey(currentStorageKey => {
        const nextStorageKey = getThemeStorageKey(nextUserId);
        return currentStorageKey === nextStorageKey ? currentStorageKey : nextStorageKey;
      });
    };

    syncThemeIdentity();
    window.addEventListener('focus', syncThemeIdentity);
    document.addEventListener('visibilitychange', syncThemeIdentity);

    return () => {
      window.removeEventListener('focus', syncThemeIdentity);
      document.removeEventListener('visibilitychange', syncThemeIdentity);
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const storedThemeMode = getStoredThemeMode(storageKey);
    const legacyThemeMode = storageKey !== THEME_STORAGE_KEY ? getStoredThemeMode(THEME_STORAGE_KEY) : null;
    const nextThemeMode = storedThemeMode || legacyThemeMode || DEFAULT_THEME_MODE;
    setIsRemotePreferenceReady(!currentUserId);

    setThemeMode(currentThemeMode => (currentThemeMode === nextThemeMode ? currentThemeMode : nextThemeMode));

    if (!storedThemeMode && legacyThemeMode && typeof window !== 'undefined') {
      window.localStorage.setItem(storageKey, legacyThemeMode);
    }

    if (!currentUserId) {
      lastPersistedRemotePreferenceRef.current = { userId: null, themeMode: null };
      return () => {
        isActive = false;
      };
    }

    fetchThemePreference()
      .then(remoteThemeMode => {
        if (!isActive || !remoteThemeMode) {
          return;
        }

        window.localStorage.setItem(storageKey, remoteThemeMode);
        lastPersistedRemotePreferenceRef.current = { userId: currentUserId, themeMode: remoteThemeMode };
        setThemeMode(currentThemeMode => (currentThemeMode === remoteThemeMode ? currentThemeMode : remoteThemeMode));
      })
      .catch(() => {})
      .finally(() => {
        if (isActive) {
          setIsRemotePreferenceReady(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [currentUserId, storageKey]);

  useEffect(() => {
    window.localStorage.setItem(storageKey, themeMode);
    document.body.classList.toggle('dashboard-theme-light', themeMode === 'light');
    document.body.classList.toggle('dashboard-theme-dark', themeMode === 'dark');
  }, [storageKey, themeMode]);

  useEffect(() => {
    if (!currentUserId || !isRemotePreferenceReady) {
      return undefined;
    }

    if (
      lastPersistedRemotePreferenceRef.current.userId === currentUserId &&
      lastPersistedRemotePreferenceRef.current.themeMode === themeMode
    ) {
      return undefined;
    }

    let isActive = true;

    persistThemePreference(themeMode)
      .then(savedThemeMode => {
        if (!isActive) {
          return;
        }

        lastPersistedRemotePreferenceRef.current = {
          userId: currentUserId,
          themeMode: savedThemeMode || themeMode,
        };
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, [currentUserId, isRemotePreferenceReady, themeMode]);

  const value = useMemo(
    () => ({
      isDarkMode: themeMode === 'dark',
      setThemeMode,
      toggleTheme: () => setThemeMode(currentMode => (currentMode === 'dark' ? 'light' : 'dark'))
    }),
    [themeMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export { ThemeContext };